import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { hasConnectedDiscord } from '../discordIdentity';
import { parseInviteCodeInput } from '../joinInvite';
import {
  JOIN_COMMISSIONER_SECONDARY_LABEL,
  JOIN_COMMISSIONER_SECONDARY_NOTE,
  JOIN_INVITE_CODE_INVALID_INPUT,
  JOIN_INVITE_CODE_LABEL,
  JOIN_INVITE_CODE_PLACEHOLDER,
  JOIN_INVITE_CODE_SUBMIT,
  JOIN_MEMBER_DETAIL,
  JOIN_MEMBER_HEADLINE,
} from '../joinMemberCopy';
import { buildDiscordConnectUrl } from '../../utils/authUrls';
import { lscJoinInvitePath, lscPath, lscReturnTo } from '../routing';
import { useLscContentColumn } from '../lscContentColumn';

const LscJoinPage: React.FC = () => {
  useLscContentColumn('narrow');
  const navigate = useNavigate();
  const { user } = useUser();
  const discordConnected = hasConnectedDiscord(user);
  const [codeInput, setCodeInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const returnTo = lscReturnTo('/join');

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const code = parseInviteCodeInput(codeInput);
    if (!code) {
      setError(JOIN_INVITE_CODE_INVALID_INPUT);
      return;
    }
    setError(null);
    navigate(lscJoinInvitePath(code));
  };

  return (
    <div className="space-y-6 max-w-lg" data-testid="lsc-join-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">{JOIN_MEMBER_HEADLINE}</h1>
        <p className="text-sm text-secondary-400">{JOIN_MEMBER_DETAIL}</p>
      </div>

      <form className="card space-y-4" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary-400">{JOIN_INVITE_CODE_LABEL}</span>
          <input
            className="input-field"
            placeholder={JOIN_INVITE_CODE_PLACEHOLDER}
            value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            data-testid="invite-code-input"
          />
        </label>
        {error && (
          <p className="text-sm text-red-300" data-testid="invite-code-input-error">
            {error}
          </p>
        )}
        <div className="flex flex-wrap gap-3 items-center">
          <button type="submit" className="btn-primary" data-testid="invite-code-submit">
            {JOIN_INVITE_CODE_SUBMIT}
          </button>
          {user ? (
            discordConnected ? (
              <span
                className="text-sm text-emerald-200"
                data-testid="join-discord-connected-badge"
              >
                Discord connected
              </span>
            ) : (
              <a
                href={buildDiscordConnectUrl(returnTo)}
                className="btn-secondary inline-block"
                data-testid="join-connect-discord-link"
              >
                Connect Discord
              </a>
            )
          ) : null}
          <Link to={lscPath('/leagues')} className="btn-secondary">
            Back to leagues
          </Link>
        </div>
      </form>

      <div
        className="rounded-lg border border-secondary-700 bg-secondary-900/40 px-4 py-3 space-y-2"
        data-testid="join-commissioner-secondary"
      >
        <p className="text-xs text-secondary-400">{JOIN_COMMISSIONER_SECONDARY_NOTE}</p>
        <Link
          to={lscPath('/pricing')}
          className="text-sm text-primary-300 hover:text-primary-200"
          data-testid="join-commissioner-pricing-link"
        >
          {JOIN_COMMISSIONER_SECONDARY_LABEL}
        </Link>
      </div>
    </div>
  );
};

export default LscJoinPage;
